/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

/**
 * Safely coerce an unknown value to a trimmed string, returning empty string for non-strings.
 */
export function safeStr(v: unknown): string {
  return typeof v === 'string' ? v.trim() : '';
}

/**
 * Build the ordered candidate list of display names from an OIDC profile object.
 *
 * Priority: full_name > name > given+family combined > given_name > family_name
 */
export function buildNameCandidates(profile: Record<string, unknown>): (unknown)[] {
  const given = safeStr(profile.given_name);
  const family = safeStr(profile.family_name);
  const combined = given && family ? `${given} ${family}` : null;
  return [profile.full_name, profile.name, combined, profile.given_name, profile.family_name];
}

/**
 * Type guard for a non-empty, non-whitespace string.
 *
 * @param value Value to test.
 * @returns `true` when `value` is a string with visible characters.
 */
function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim() !== '';
}

/**
 * Find the first non-empty string in an array of candidates.
 */
export function firstNonEmptyString(candidates: unknown[]): string {
  const found = candidates.find(isNonEmptyString);
  return found ?? '';
}

/**
 * Resolves the authenticated user's display name from a profile object, trying several
 * claim fields in priority order (full_name, name, given+family, given, family).
 */
export function resolveFullName(profile: Record<string, unknown> | undefined): string {
  if (!profile) return '';
  return firstNonEmptyString(buildNameCandidates(profile));
}
